import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Box } from "@mui/material";
import IosShareIcon from "@mui/icons-material/IosShare";
import { LoadingButton } from "@mui/lab";
import { startCase, union } from "lodash";
import { Grid, Stack, Tooltip, Typography } from "@financeable-com-au/financeable-ui";
import {
  getIsCommercialOrBusinessApplication,
  ERRORS_MESSAGE,
  SUCCESS_MESSAGE,
  APPLICATION_SECTIONS_ID,
  APPLICATION_DOWNLOAD_TYPE,
  getIsAssetLoan,
} from "@/constants";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useDemoCommercialApplication } from "@/hooks";
import { userPermissionsSelector } from "@/store/slices/userSlice";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { DownloadApplicationButton } from "@/components/CommonComponents/DownloadApplicationButton";
import {
  applicationSelector,
  assetSelector,
  getApplicationDownloadUrl,
  loanDetailsSelector,
  submitApplicationToEmail,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import { getCustomerRole } from "../Utils/helperFunction";
import { ApplicationProgressBar } from "../Application/ApplicationProgressBar";
import { useCalculateApplication } from "../../hooks/useCalculateApplication/useCalculateApplication";

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
};

const assetDetailsItems = ["ageOfAsset", "assetType", "assetValue", "typeOfSale", "make", "model"];

const assetDetailsItemsGlasses = ["ageOfAsset", "assetType", "assetValue", "typeOfSale", "glassesMake", "glassesModel"];

const customerDetailsItems = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "emails",
  "phoneNumbers",
  "addresses",
  "livingArrangements",
  "IDNumber",
];

const primaryCustomerDetailsItemsCommercial = ["customerAssets", "liabilities", "privacy"];

const primaryCustomerDetailsItemsConsumer = [
  "customerAssets",
  "liabilities",
  "income",
  "expenses",
  "employers",
  "privacy",
];

const remainingItemsDefaultStyle = {
  cursor: "pointer",
  fontSize: "0.875rem",
  fontWeight: 500,
  display: "block",
  padding: "8px 0",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "action.hover",
    textDecoration: "none",
  },
};

const LoanSummary = memo(({ handleApplicationComplete }) => {
  const dispatch = useDispatch();
  const { getBrokerageAmount, calculateQuoteRepayments } = useCalculateApplication();
  const { accessComponents } = useAccessAppComponent();
  const { defaultApplicationDownloadType } = accessComponents.application;
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const { assetValue = 0 } = useSelector(assetSelector);
  const {
    submittingApplicationToEmail,
    downloadApplicationLoader,
    demoLoanAmount = 0,
    demoInterestRate = 0,
    demoEstimatedValue = 0,
    demoLoanDetailsFields = {},
  } = useSelector(userDetailsSelector);
  const {
    deposit = 0,
    tradeIn = 0,
    payout = 0,
    originationFee = 0,
    requestedLoanAmount = 0,
    brokerage = 0,
  } = useSelector(loanDetailsSelector);
  const permissions = useSelector(userPermissionsSelector);
  const application = useSelector(applicationSelector);

  const { applicationType } = application;
  const isAssetLoan = getIsAssetLoan(applicationType);
  const brokerageAmount = getBrokerageAmount({ brokeragePercentage: Number(brokerage || 0) });
  const netAssetValue = 1 * +assetValue - +deposit - +tradeIn + +payout || 0;
  const principalAmount = isAssetLoan ? netAssetValue : requestedLoanAmount;
  // For referrer only origination fees is available so it's use as financeFees
  const totalFinancedFees = Number(originationFee) || 0;
  const financeAmount = principalAmount + totalFinancedFees + brokerageAmount;
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);

  const primaryCustomerDetailsItems = isCommercialOrBusinessApplication
    ? primaryCustomerDetailsItemsCommercial
    : primaryCustomerDetailsItemsConsumer;
  const primaryCustomerDetailsItemsCount = primaryCustomerDetailsItems.length + customerDetailsItems.length;

  const [applicationProgress, setApplicationProgress] = React.useState(0);
  const customerRole = getCustomerRole(application.applicationType, application.entities, application.purpose);
  const isApplicationComplete = applicationProgress === 100;
  const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;

  // Only for demo purpose
  const LVR = (+demoLoanAmount / (+demoEstimatedValue ? demoEstimatedValue : demoLoanAmount)) * 100;
  const repaymentAmount = calculateQuoteRepayments({
    financeAmount: demoLoanAmount,
    balloon: 0,
    rate: Number(demoInterestRate),
    isInArrears: true,
  });

  function handleAssetItemsProgress() {
    let progress = 0;
    let complete = [];
    let assetList =
      application?.asset?.model || application?.asset?.make ? assetDetailsItems : assetDetailsItemsGlasses;
    for (let [key, value] of Object.entries(application?.asset)) {
      if (["glassesMake", "glassesModel"].includes(key)) {
        if (value && value?.label && value?.label !== "") {
          progress++;
          complete.push(key);
        }
      } else if (assetList.includes(key) && value && value !== "") {
        progress++;
        complete.push(key);
      }
    }

    complete = assetList?.reduce((acc, val) => {
      acc.push(
        <Box key={val} sx={{ py: 0.5 }}>
          <Typography style={complete?.includes(val) ? { textDecoration: "line-through" } : {}}>
            {startCase(val)}
          </Typography>
        </Box>,
      );
      return acc;
    }, []);

    return { progress, complete };
  }

  function handleCustomerItemsProgress(i) {
    let progress = 0;
    let complete = [];
    let items = [];

    if (i === 0) items = union(customerDetailsItems, primaryCustomerDetailsItems);
    else items = customerDetailsItems;

    for (let [key, value] of Object.entries(application?.customers?.[i] || {})) {
      if ((items.includes(key) && value && value !== "") || key === "documents" || key === "customerProfile") {
        if (key === "emails") {
          if (application?.customers?.[i]?.emails[0]) {
            progress++;
            complete.push(key);
            continue;
          }
          continue;
        }
        if (key === "phoneNumbers") {
          if (application?.customers?.[i]?.phoneNumbers[0]) {
            progress++;
            complete.push(key);
            continue;
          }
          continue;
        }
        if (key === "addresses") {
          if (application?.customers?.[i]?.addresses.length > 0) {
            progress++;
            complete.push(key);
            continue;
          }
          continue;
        }

        if (applicationType === ApplicationType.Consumer && key === "employers") {
          if (application?.customers?.[i]?.employers?.[0]) {
            progress++;
            complete.push("employers");
            continue;
          }
          continue;
        }
        if (key === "documents") {
          const hasPrivacy =
            application?.customers?.[i]?.documents?.length > 0 &&
            application?.customers?.[i]?.documents?.filter((doc) => doc.name?.includes("Privacy"));

          if (i === 0) {
            if (hasPrivacy) {
              progress++;
              complete.push("privacy");
            }
          }
          continue;
        }

        if (key === "customerProfile") {
          if (application?.customers?.[i]?.customerProfile?.livingArrangements) {
            progress++;
            complete.push("livingArrangements");
            continue;
          }
          continue;
        }

        if (value.items) {
          const hasValue = value.items.reduce((acc, val) => (val.value ? true : acc), false);
          if (hasValue) {
            progress++;
            complete.push(key);
            continue;
          }
          continue;
        }

        if (items.includes(key) && value && value !== "") {
          progress++;
          complete.push(key);
        }
        continue;
      }
    }

    const completeItems = items?.reduce((acc, val) => {
      let displayVal = val;
      if (val === "addresses") displayVal = "address";
      else if (val === "emails") displayVal = "email";
      else if (val === "phoneNumbers") displayVal = "phoneNumber";
      const isComplete = complete?.includes(val);

      acc.push(
        <Box key={val} sx={{ py: 0.5 }}>
          <Typography
            style={{
              textDecoration: isComplete ? "line-through" : "none",
              color: isComplete ? "text.secondary" : "text.primary",
              fontSize: "0.875rem",
            }}
          >
            {startCase(displayVal)}
          </Typography>
        </Box>,
      );
      return acc;
    }, []);

    return { progress, complete: completeItems };
  }

  useEffect(() => {
    const assetProgress = handleAssetItemsProgress()?.progress;
    const entityProgress = application?.entities?.[0] ? 1 : 0;
    const customersProgress = application?.customers?.map((val, i) => handleCustomerItemsProgress(i)?.progress);
    const customersProgressTotal = customersProgress?.reduce((acc, val) => acc + val, 0);
    const totalRequiredItems =
      (applicationType === ApplicationType.Commercial ? 1 : 0) +
      (isDemoCommercialApplication ? 0 : assetDetailsItems.length) +
      primaryCustomerDetailsItems.length +
      application?.customers?.length * customerDetailsItems.length;
    const totalProgress = (isDemoCommercialApplication ? 0 : assetProgress) + entityProgress + customersProgressTotal;
    setApplicationProgress(
      isNaN(parseInt((totalProgress / totalRequiredItems) * 100))
        ? 0
        : parseInt((totalProgress / totalRequiredItems) * 100),
    );

    handleApplicationComplete(totalProgress / totalRequiredItems >= 1);
  }, [application]);

  const handle = {
    handleApplicationDownload: (downloadType) => {
      let downloadApplicationPayload = { applicationId: application._id, downloadType };
      if (isDemoCommercialApplication) {
        downloadApplicationPayload = {
          ...downloadApplicationPayload,
          additionalLoanDetails: {
            ...demoLoanDetailsFields,
            repayments: repaymentAmount,
            lvr: (LVR ? LVR : 0)?.toFixed(2),
          },
        };
      }
      // call api to generate excel for application
      dispatch(getApplicationDownloadUrl(downloadApplicationPayload)).then((res) => {
        if (res.error) {
          // Show snackbar.
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          // Show snackbar.
          // viewFile(res?.payload?.data?.data);
        }
      });
    },
    handleSubmitApplication: () => {
      dispatch(submitApplicationToEmail({ applicationId: application._id }))
        // @ts-expect-error: existing dispatch
        .then((res) => {
          if (res.error) {
            // Show snackbar.
            dispatch(
              openSnackbar({
                message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          } else {
            // Show snackbar.
            dispatch(
              openSnackbar({
                message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.applicationSubmitted,
                intent: "success",
              }),
            );
          }
        });
    },
  };

  const CustomTooltip = ({ title, children, placement = "right" }) => {
    if (!title || (Array.isArray(title) && title.length === 0)) {
      return <>{children}</>;
    }

    return (
      <Tooltip
        arrow
        placement={placement}
        title={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              py: 0.5,
              maxWidth: "200px",
            }}
          >
            {Array.isArray(title) ? (
              title
            ) : (
              <Typography style={{ fontSize: "0.875rem", textDecoration: "line-through" }}>{title}</Typography>
            )}
          </Box>
        }
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(0, 0, 0, 0.87)",
              padding: "8px 12px",
              borderRadius: "8px",
              maxWidth: "220px",
            },
          },
          arrow: {
            sx: {
              color: "rgba(0, 0, 0, 0.87)",
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <Grid container xs={12} sm={12} md={12} lg={12}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          padding: "0",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginTop: "20px",
        }}
      >
        <Typography level="title-md">Loan Summary</Typography>

        <Box
          sx={{
            padding: "16px",
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            marginBottom: "16px",
          }}
        >
          {!isDemoCommercialApplication ? (
            <>
              <Typography
                level="body-md"
                style={{
                  marginBottom: "15px",
                  fontWeight: 500,
                }}
              >
                Finance amount
              </Typography>
              <Typography level="title-md">{financeAmount.toLocaleString("en-US", dollarStringOptions)}</Typography>
            </>
          ) : (
            <Stack gap={5}>
              <Typography level="body-md">
                <strong>Repayment:</strong> {repaymentAmount.toLocaleString("en-US", dollarStringOptions)}
              </Typography>
              <Typography level="body-md">
                <strong>LVR:</strong> {`${(LVR ? LVR : 0)?.toFixed(2)}%`}
              </Typography>
              <Typography level="body-md">
                <strong>Brokerage:</strong> {(demoLoanDetailsFields?.brokerageAmount || 0)?.toLocaleString("en-US", dollarStringOptions)} ({demoLoanDetailsFields?.brokerage || 0}%)
              </Typography>
              <Typography level="body-md">
                <strong>Estabilishment Fee:</strong> {(demoLoanDetailsFields?.estabilishmentFeeAmount || 0)?.toLocaleString("en-US", dollarStringOptions)} ({demoLoanDetailsFields?.estabilishmentFee || 0}%)
              </Typography>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            padding: "20px",
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Typography level="title-sm" marginBottom="15px">
            Application progress:
          </Typography>

          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              marginBottom: "20px",
            }}
          >
            <ApplicationProgressBar value={applicationProgress} />
          </Box>

          <Box>
            <Typography level="title-sm" marginBottom="15px">
              Items remaining:
            </Typography>

            <Stack direction="column" spacing={1.5}>
              {!isDemoCommercialApplication && (
                <CustomTooltip title={handleAssetItemsProgress()?.complete}>
                  <Link
                    underline="hover"
                    sx={{
                      ...remainingItemsDefaultStyle,
                      textDecoration: handleAssetItemsProgress()?.progress === 6 ? "line-through" : "none",
                      color: handleAssetItemsProgress()?.progress === 6 ? "text.secondary" : "primary.main",
                    }}
                    onClick={() => {
                      if (document.getElementById(APPLICATION_SECTIONS_ID.ASSETS))
                        document
                          .getElementById(APPLICATION_SECTIONS_ID.ASSETS)
                          .scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    Asset details: {handleAssetItemsProgress()?.progress}/6
                  </Link>
                </CustomTooltip>
              )}

              {applicationType === ApplicationType.Commercial && (
                <Link
                  underline="hover"
                  sx={{
                    ...remainingItemsDefaultStyle,
                    textDecoration: application?.entities?.[0] ? "line-through" : "none",
                    color: application?.entities?.[0] ? "text.secondary" : "primary.main",
                  }}
                  onClick={() => {
                    if (document.getElementById("addEntity"))
                      document.getElementById("addEntity").scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  {application?.entities?.[0] ? "Entity" : "Add Entity"}
                </Link>
              )}

              {application?.customers?.[0] ? (
                application?.customers?.map((customer, i) => (
                  <React.Fragment key={`customer-${i}`}>
                    <CustomTooltip title={handleCustomerItemsProgress(i)?.complete} placement="right">
                      <Link
                        underline="hover"
                        sx={{
                          ...remainingItemsDefaultStyle,
                          textDecoration:
                            handleCustomerItemsProgress(i).progress /
                              (i === 0 ? primaryCustomerDetailsItemsCount : customerDetailsItems.length) ===
                              1
                              ? "line-through"
                              : "none",
                          color:
                            handleCustomerItemsProgress(i).progress /
                              (i === 0 ? primaryCustomerDetailsItemsCount : customerDetailsItems.length) ===
                              1
                              ? "text.secondary"
                              : "primary.main",
                        }}
                        onClick={() => {
                          if (document.getElementById(`customer-${customer?._id}`))
                            document
                              .getElementById(`customer-${customer?._id}`)
                              .scrollIntoView({ behavior: "smooth", block: "nearest" });
                        }}
                      >
                        {startCase(customerRole)} {i + 1} details: {handleCustomerItemsProgress(i).progress}/
                        {i === 0 ? primaryCustomerDetailsItemsCount : customerDetailsItems.length}
                      </Link>
                    </CustomTooltip>

                    {applicationType === ApplicationType.Consumer && (
                      <Link
                        underline="hover"
                        sx={{
                          ...remainingItemsDefaultStyle,
                          textDecoration: application?.customers?.[i].employers?.[0] ? "line-through" : "none",
                          color: application?.customers?.[i].employers?.[0] ? "text.secondary" : "primary.main",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          if (document.getElementById("addEmployer"))
                            document
                              .getElementById("addEmployer")
                              .scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                      >
                        {application?.customers?.[i].employers?.[0] ? "Employer" : "Add employer 1"}
                      </Link>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <Link
                  underline="hover"
                  sx={{
                    ...remainingItemsDefaultStyle,
                  }}
                  onClick={() => {
                    if (document.getElementById(`add${customerRole}`))
                      document
                        .getElementById(`add${customerRole}`)
                        .scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Add {startCase(customerRole)}
                </Link>
              )}
            </Stack>

            <Grid container spacing={2} style={{ marginTop: "30px" }}>
              <Grid xs={12} sm={6} md={7} lg={7}>
                <DownloadApplicationButton
                  defaultDownloadType={defaultApplicationDownloadType || APPLICATION_DOWNLOAD_TYPE.PDF}
                  isLoading={downloadApplicationLoader}
                  disabled={isDisableEdit}
                  onDownload={handle.handleApplicationDownload}
                />
              </Grid>
              <Grid xs={12} sm={6} md={5} lg={5}>
                <LoadingButton
                  endIcon={<IosShareIcon />}
                  loadingPosition="end"
                  variant="contained"
                  size="small"
                  disabled={isDisableEdit || submittingApplicationToEmail || !isApplicationComplete}
                  onClick={handle.handleSubmitApplication}
                >
                  {submittingApplicationToEmail ? "submitting..." : "Submit"}
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
});

export default LoanSummary;
